import React from 'react';
import { X, User, Calendar, MapPin, Info, Phone, Mail, FileText, CheckCircle2, XCircle, Clock, Building2, Shield } from 'lucide-react';
import { formatDate as stringUtilsFormatDate } from '../../utils/stringUtils';

const defaultFormatDate = (dateValue, options = { includeTime: false }) => {
  if (!dateValue) return '-';
  return stringUtilsFormatDate(dateValue, options);
};

const defaultGetEmployeeName = (item) => {
  if (!item?.employeeId) return 'Employee';
  if (item.employeeId.personalInfo) {
    return `${item.employeeId.personalInfo.firstName || ''} ${item.employeeId.personalInfo.lastName || ''}`.trim() || 'Employee';
  }
  return 'Employee';
};

const WfhDetailsModal = ({
  isOpen,
  onClose,
  request,
  formatDate = defaultFormatDate,
  getEmployeeName = defaultGetEmployeeName
}) => {
  if (!isOpen || !request) return null;

  const getStatusStyle = (status) => {
    switch (status) {
      case 'approved':
        return {
          bg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
          icon: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
          text: 'Approved'
        };
      case 'rejected':
        return {
          bg: 'bg-rose-50 text-rose-700 border-rose-200',
          icon: <XCircle className="h-4 w-4 text-rose-600" />,
          text: 'Rejected'
        };
      default:
        return {
          bg: 'bg-amber-50 text-amber-700 border-amber-200',
          icon: <Clock className="h-4 w-4 text-amber-600 animate-pulse" />,
          text: 'Pending'
        };
    }
  };

  const statusStyle = getStatusStyle(request.status);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl border border-gray-100 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all scale-100 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white rounded-t-2xl">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              WFH Request Details
            </h3>
            <p className="text-xs text-gray-400 font-mono">ID: {request.requestId}</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 text-sm">
          
          {/* Main Info Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border border-gray-100 rounded-xl">
            <div className="space-y-1">
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Status
              </div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusStyle.bg}`}>
                {statusStyle.icon}
                {statusStyle.text}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> WFH Type
              </div>
              <div className="text-gray-950 font-medium capitalize">
                {request.wfhType ? request.wfhType.replace(/_/g, ' ') : 'Single Day'}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Date Range
              </div>
              <div className="text-gray-950 font-medium">
                {formatDate(request.fromDate)}
                {request.wfhType === 'multiple_days' && (
                  <span className="text-gray-400 block text-xs">to {formatDate(request.toDate)}</span>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase font-bold tracking-wider text-gray-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Total Days
              </div>
              <div className="text-gray-950 font-medium">
                {request.totalDays} {request.totalDays === 1 ? 'day' : 'days'}
              </div>
            </div>
          </div>

          {/* Grid Layout: Employee Details & Reason */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Employee Card */}
            <div className="border border-gray-150 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3 mb-3">
                <div className="p-1.5 bg-blue-50 text-[#0F2942] rounded-lg">
                  <User className="h-4 w-4" />
                </div>
                <div className="font-semibold text-gray-800">Employee Details</div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="text-gray-900 font-medium">{getEmployeeName(request)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Employee ID:</span>
                  <span className="text-gray-900 font-medium font-mono">{request.employeeId?.employeeId || '-'}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500">Email:</span>
                  <span className="text-gray-900 font-medium break-all text-right">
                    {request.employeeId?.user?.email || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Role:</span>
                  <span className="text-gray-900 font-medium capitalize">{request.employeeId?.user?.role?.replace(/_/g, ' ') || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Department:</span>
                  <span className="text-gray-900 font-medium capitalize">{request.employeeId?.employmentInfo?.department || '-'}</span>
                </div>
              </div>
            </div>

            {/* Work Setup details */}
            <div className="border border-gray-150 rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3 mb-3">
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <MapPin className="h-4 w-4" />
                </div>
                <div className="font-semibold text-gray-800">Setup Details</div>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="text-gray-900 font-medium capitalize">{request.workLocation || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="text-gray-900 font-medium">
                    {typeof request.reasonCategory === 'object' ? request.reasonCategory?.name : request.reasonCategory || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created Date:</span>
                  <span className="text-gray-900 font-medium">{formatDate(request.createdAt, { includeTime: true })}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {request.emergencyContact?.name && (
            <div className="border border-gray-150 rounded-xl p-4 bg-white shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3 mb-3">
                <div className="p-1.5 bg-rose-50 text-rose-600 rounded-lg">
                  <Phone className="h-4 w-4" />
                </div>
                <div className="font-semibold text-gray-800">Emergency Contact Info</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <div className="space-y-1">
                  <span className="text-gray-400 block">Name</span>
                  <span className="text-gray-900 font-medium">{request.emergencyContact.name}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-gray-400 block">Email</span>
                  <span className="text-gray-950 font-medium break-all">{request.emergencyContact.email || '-'}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-gray-400 block">Phone</span>
                  <span className="text-gray-955 font-medium">{request.emergencyContact.phone || '-'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Request Reason */}
          <div className="space-y-1.5">
            <div className="font-semibold text-gray-800 flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-gray-500" />
              Reason for Request
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-gray-700 leading-relaxed font-sans first-letter:capitalize shadow-inner">
              {request.reason || '-'}
            </div>
          </div>

          {/* Approval Decision Card */}
          <div className="space-y-3">
            <div className="font-semibold text-gray-800 flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-gray-500" />
              Approval Details
            </div>
            {request.approvals?.status !== 'pending' ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs uppercase tracking-wider font-bold">Decision:</span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      request.approvals.status === 'approved' 
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                        : 'bg-rose-50 text-rose-700 border border-rose-200'
                    }`}>
                      {request.approvals.status === 'approved' ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : (
                        <XCircle className="h-3 w-3" />
                      )}
                      {request.approvals.status}
                    </span>
                  </div>
                  <span className="text-gray-400 text-xs">
                    {formatDate(request.approvals.actedAt, { includeTime: true })}
                  </span>
                </div>
                
                {request.approvals.actedBy && (
                  <div className="p-4 bg-white space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div className="flex justify-between border-b border-gray-50 pb-1.5">
                        <span className="text-gray-500">Approver Name:</span>
                        <span className="text-gray-900 font-medium">
                          {`${request.approvals.actedBy.firstName} ${request.approvals.actedBy.lastName || ''}`.trim()}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-gray-50 pb-1.5">
                        <span className="text-gray-500">Approver Email:</span>
                        <span className="text-gray-900 font-medium break-all">{request.approvals.actedBy.email || '-'}</span>
                      </div>
                      <div className="flex justify-between border-b border-gray-50 pb-1.5">
                        <span className="text-gray-500">Role:</span>
                        <span className="text-gray-900 font-medium capitalize">
                          {request.approvals.actedBy.role?.replace(/_/g, ' ') || '-'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-gray-50 pb-1.5">
                        <span className="text-gray-500">Department:</span>
                        <span className="text-gray-900 font-medium capitalize">
                          {request.approvals.department || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {request.approvals.comments && (
                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1">Approver Comments</span>
                    <div className="text-gray-700 bg-white p-3 rounded-lg border border-gray-100 italic first-letter:capitalize">
                      "{request.approvals.comments}"
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2.5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                <Clock className="h-5 w-5 text-amber-600 animate-pulse flex-shrink-0" />
                <div className="text-xs font-medium">Awaiting decision from the reporting manager or HR department.</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium rounded-xl bg-[#0F2942] text-white hover:bg-[#0B2033] transition-all duration-200 shadow-md shadow-blue-500/10 hover:shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98]"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default WfhDetailsModal;
